import type { EquipmentPreference, ConstrainedInverterSelection } from '../services/equipmentSelectionService';
import type { UserType } from '../types';

export interface ConstrainedDcSizingInput {
  selectedTotalAcKw: number;
  modulePowerW: number;
  maxDcAcRatio?: number;
}

export interface ConstrainedDcSizingResult {
  panelCount: number;
  dcTargetKw: number;
  finalSystemSizeKw: number;
  finalDcAcRatio: number;
}

export interface ResolveInverterConstrainedSizingInput {
  initialSystemSizeKw: number;
  modulePowerW: number;
  userType: UserType;
  preference: EquipmentPreference;
  maxDcAcRatio?: number;
  selectSingle: (
    preference: EquipmentPreference,
    maxAcKw: number
  ) => Promise<ConstrainedInverterSelection>;
  selectMulti: (
    preference: EquipmentPreference,
    maxAcKw: number
  ) => Promise<ConstrainedInverterSelection>;
}

export interface ResolveInverterConstrainedSizingResult {
  acCapKw: number;
  selection: ConstrainedInverterSelection;
  panelCount: number;
  dcTargetKw: number;
  finalSystemSizeKw: number;
  finalDcAcRatio: number;
}

export function computeConstrainedDcSizing(input: ConstrainedDcSizingInput): ConstrainedDcSizingResult {
  const maxDcAcRatio =
    typeof input.maxDcAcRatio === 'number' && Number.isFinite(input.maxDcAcRatio) && input.maxDcAcRatio > 0
      ? input.maxDcAcRatio
      : 1.3;
  const selectedTotalAcKw =
    typeof input.selectedTotalAcKw === 'number' && Number.isFinite(input.selectedTotalAcKw) && input.selectedTotalAcKw > 0
      ? input.selectedTotalAcKw
      : 0;
  const modulePowerW =
    typeof input.modulePowerW === 'number' && Number.isFinite(input.modulePowerW) && input.modulePowerW > 0
      ? input.modulePowerW
      : 550;

  if (selectedTotalAcKw <= 0) {
    return {
      panelCount: 0,
      dcTargetKw: 0,
      finalSystemSizeKw: 0,
      finalDcAcRatio: 0,
    };
  }

  const dcTargetKw = selectedTotalAcKw * maxDcAcRatio;
  let panelCount = Math.max(1, Math.floor((dcTargetKw * 1000) / modulePowerW));
  let finalSystemSizeKw = (panelCount * modulePowerW) / 1000;

  while (
    panelCount > 1 &&
    selectedTotalAcKw > 0 &&
    finalSystemSizeKw / selectedTotalAcKw > maxDcAcRatio + 1e-9
  ) {
    panelCount -= 1;
    finalSystemSizeKw = (panelCount * modulePowerW) / 1000;
  }

  const finalDcAcRatio = selectedTotalAcKw > 0 ? finalSystemSizeKw / selectedTotalAcKw : 0;
  return {
    panelCount,
    dcTargetKw,
    finalSystemSizeKw,
    finalDcAcRatio,
  };
}

export async function resolveInverterConstrainedSizing(
  input: ResolveInverterConstrainedSizingInput
): Promise<ResolveInverterConstrainedSizingResult> {
  const maxDcAcRatio =
    typeof input.maxDcAcRatio === 'number' && Number.isFinite(input.maxDcAcRatio) && input.maxDcAcRatio > 0
      ? input.maxDcAcRatio
      : 1.3;
  const initialSystemSizeKw =
    typeof input.initialSystemSizeKw === 'number' && Number.isFinite(input.initialSystemSizeKw) && input.initialSystemSizeKw > 0
      ? input.initialSystemSizeKw
      : 0;

  const acCapKw = initialSystemSizeKw / maxDcAcRatio;
  const selection =
    input.userType === 'residential'
      ? await input.selectSingle(input.preference, acCapKw)
      : await input.selectMulti(input.preference, acCapKw);

  const constrainedDc = computeConstrainedDcSizing({
    selectedTotalAcKw: selection.selectedTotalAcKw,
    modulePowerW: input.modulePowerW,
    maxDcAcRatio,
  });

  return {
    acCapKw,
    selection,
    panelCount: constrainedDc.panelCount,
    dcTargetKw: constrainedDc.dcTargetKw,
    finalSystemSizeKw: constrainedDc.finalSystemSizeKw,
    finalDcAcRatio: constrainedDc.finalDcAcRatio,
  };
}
